import { hasValidCoordinates } from "./geo";
import { partnerOrgLeadConvertComments } from "./partner-organization-lead-contact";

export const VOLUNTEER_ORG_EVENT_TYPES = [
  {
    id: "routine",
    field: "interestedInRoutine",
    label: "אירוע שגרתי",
  },
  {
    id: "outbreak",
    field: "interestedInOutbreak",
    label: "אירוע מתפרץ",
  },
  {
    id: "emergency",
    field: "interestedInEmergency",
    label: "חירום",
  },
] as const;

export type VolunteerOrgEventTypeId =
  (typeof VOLUNTEER_ORG_EVENT_TYPES)[number]["id"];

export const VOLUNTEER_ORG_CONTACT_ROLES = [
  {
    id: "volunteeringCoordinator",
    nameKey: "volunteeringCoordinatorName",
    phoneKey: "volunteeringCoordinatorPhone",
    label: "אחראי התנדבות",
  },
  {
    id: "emergency",
    nameKey: "emergencyContactName",
    phoneKey: "emergencyContactPhone",
    label: "איש קשר חירום",
  },
  {
    id: "head",
    nameKey: "headName",
    phoneKey: "headPhone",
    label: "ראש",
  },
  {
    id: "extra",
    nameKey: "extraContactName",
    phoneKey: "extraContactPhone",
    label: "איש קשר נוסף",
  },
] as const;

export type VolunteerOrgContactFields = {
  headName?: string | null;
  headPhone?: string | null;
  volunteeringCoordinatorName?: string | null;
  volunteeringCoordinatorPhone?: string | null;
  emergencyContactName?: string | null;
  emergencyContactPhone?: string | null;
  extraContactName?: string | null;
  extraContactPhone?: string | null;
};

export const VOLUNTEER_ORG_CONTACT_ROLE_KINDS = [
  "volunteeringCoordinator",
  "emergency",
  "head",
  "extra",
  "other",
] as const;

export type VolunteerOrgContactRoleKind =
  (typeof VOLUNTEER_ORG_CONTACT_ROLE_KINDS)[number];

export function isVolunteerOrgContactRoleKind(
  value: string | null | undefined,
): value is VolunteerOrgContactRoleKind {
  return (
    value === "volunteeringCoordinator" ||
    value === "emergency" ||
    value === "head" ||
    value === "extra" ||
    value === "other"
  );
}

export const CANONICAL_VOLUNTEER_ORG_CONTACTS = [
  {
    roleKind: "volunteeringCoordinator" as const,
    roleLabel: "אחראי התנדבות",
    sortOrder: 0,
  },
  { roleKind: "emergency" as const, roleLabel: "איש קשר חירום", sortOrder: 1 },
  { roleKind: "head" as const, roleLabel: "ראש", sortOrder: 2 },
  { roleKind: "extra" as const, roleLabel: "איש קשר נוסף", sortOrder: 3 },
];

export type VolunteerOrgContactDraft = {
  id?: string;
  clientKey: string;
  roleKind: VolunteerOrgContactRoleKind;
  roleLabel: string;
  name: string;
  phone: string;
  email: string;
  sortOrder: number;
};

export type VolunteerOrgStoredContact = {
  id?: string;
  roleKind?: string | null;
  roleLabel?: string | null;
  name?: string | null;
  phone?: string | null;
  email?: string | null;
  sortOrder?: number | null;
};

let contactClientKey = 0;

function nextContactClientKey() {
  contactClientKey += 1;
  return `contact-${contactClientKey}`;
}

export function defaultVolunteerOrgContactDrafts(): VolunteerOrgContactDraft[] {
  return CANONICAL_VOLUNTEER_ORG_CONTACTS.map((role) => ({
    clientKey: nextContactClientKey(),
    roleKind: role.roleKind,
    roleLabel: role.roleLabel,
    name: "",
    phone: "",
    email: "",
    sortOrder: role.sortOrder,
  }));
}

export function legacyVolunteerOrgContactDrafts(
  fields: VolunteerOrgContactFields,
): VolunteerOrgContactDraft[] {
  return VOLUNTEER_ORG_CONTACT_ROLES.map((role, index) => ({
    clientKey: nextContactClientKey(),
    roleKind: role.id,
    roleLabel: role.label,
    name: fields[role.nameKey]?.trim() ?? "",
    phone: fields[role.phoneKey]?.trim() ?? "",
    email: "",
    sortOrder: index,
  }));
}

function draftFromStored(
  row: VolunteerOrgStoredContact,
  fallbackLabel: string,
  sortOrder: number,
): VolunteerOrgContactDraft | null {
  if (!isVolunteerOrgContactRoleKind(row.roleKind)) return null;
  return {
    id: row.id,
    clientKey: row.id || nextContactClientKey(),
    roleKind: row.roleKind,
    roleLabel: row.roleLabel?.trim() || fallbackLabel,
    name: row.name?.trim() ?? "",
    phone: row.phone?.trim() ?? "",
    email: row.email?.trim() ?? "",
    sortOrder: row.sortOrder ?? sortOrder,
  };
}

export function volunteerOrgContactDrafts(source: VolunteerOrgContactFields & {
  contacts?: VolunteerOrgStoredContact[] | null;
}): VolunteerOrgContactDraft[] {
  const stored = (source.contacts ?? [])
    .map((row, index) =>
      draftFromStored(row, "איש קשר", row.sortOrder ?? index),
    )
    .filter((row): row is VolunteerOrgContactDraft => row != null);
  if (stored.length === 0) return legacyVolunteerOrgContactDrafts(source);

  const extras = stored
    .filter((row) => row.roleKind === "other")
    .sort((a, b) => a.sortOrder - b.sortOrder);
  return [
    ...CANONICAL_VOLUNTEER_ORG_CONTACTS.map((role) => {
      const match = stored.find((row) => row.roleKind === role.roleKind);
      return (
        match ?? {
          clientKey: nextContactClientKey(),
          roleKind: role.roleKind,
          roleLabel: role.roleLabel,
          name: "",
          phone: "",
          email: "",
          sortOrder: role.sortOrder,
        }
      );
    }),
    ...extras,
  ];
}

export function newVolunteerOrgContactDraft(
  sortOrder: number,
): VolunteerOrgContactDraft {
  return {
    clientKey: nextContactClientKey(),
    roleKind: "other",
    roleLabel: "",
    name: "",
    phone: "",
    email: "",
    sortOrder,
  };
}

export function canonicalVolunteerOrgContactColumns(
  drafts: VolunteerOrgContactDraft[],
): VolunteerOrgContactFields {
  const byKind = new Map(drafts.map((row) => [row.roleKind, row]));
  const value = (kind: VolunteerOrgContactRoleKind, field: "name" | "phone") =>
    emptyToNull(byKind.get(kind)?.[field]);
  return {
    volunteeringCoordinatorName: value("volunteeringCoordinator", "name"),
    volunteeringCoordinatorPhone: value("volunteeringCoordinator", "phone"),
    emergencyContactName: value("emergency", "name"),
    emergencyContactPhone: value("emergency", "phone"),
    headName: value("head", "name"),
    headPhone: value("head", "phone"),
    extraContactName: value("extra", "name"),
    extraContactPhone: value("extra", "phone"),
  };
}

export function normalizeVolunteerOrgContactDrafts(
  drafts: VolunteerOrgContactDraft[],
): VolunteerOrgContactDraft[] {
  const canonical = CANONICAL_VOLUNTEER_ORG_CONTACTS.map((role) => {
    const match = drafts.find((row) => row.roleKind === role.roleKind);
    return normalizeOneContact(
      match ?? {
        clientKey: role.roleKind,
        roleKind: role.roleKind,
        roleLabel: role.roleLabel,
        name: "",
        phone: "",
        email: "",
        sortOrder: role.sortOrder,
      },
      role.roleLabel,
    );
  });
  const extras = drafts
    .filter((row) => row.roleKind === "other")
    .map((row, index) => normalizeOneContact(row, "איש קשר"))
    .filter(
      (row) => row.name || row.phone || row.email || row.roleLabel.trim(),
    )
    .map((row, index) => ({ ...row, sortOrder: 4 + index }));
  return [...canonical, ...extras];
}

function normalizeOneContact(
  row: VolunteerOrgContactDraft,
  fallbackLabel: string,
): VolunteerOrgContactDraft {
  const name = row.name.trim();
  const phone = row.phone.trim();
  const email = row.email.trim();
  const roleLabel = row.roleLabel.trim() || (row.roleKind === "other" ? "" : fallbackLabel);
  if ((phone || email) && !name) {
    throw new Error(
      `יש למלא שם עבור ${roleLabel || "איש קשר"}`,
    );
  }
  if (row.roleKind === "other" && name && !roleLabel) {
    throw new Error("יש למלא תפקיד לאיש קשר נוסף");
  }
  return {
    ...row,
    name,
    phone,
    email,
    roleLabel,
  };
}

export type VolunteerOrgEventFlags = {
  interestedInRoutine?: boolean;
  interestedInOutbreak?: boolean;
  interestedInEmergency?: boolean;
};

export function emptyToNull(value?: string | null) {
  const trimmed = value?.trim() ?? "";
  return trimmed ? trimmed : null;
}

export function isVolunteerOrgBranch(org: { parentId?: string | null; }) {
  return Boolean(org.parentId);
}

export function volunteerOrgLeadToParentPrefill(lead: {
  name: string;
  contactChannel: string;
  handlingManagerId?: string | null;
  phoneNumber?: string | null;
}): {
  name: string;
  comments: string;
  handlingManagerId?: string;
  volunteeringCoordinatorPhone?: string;
} {
  const handlingManagerId = lead.handlingManagerId?.trim();
  const phoneNumber = lead.phoneNumber?.trim();
  return {
    name: lead.name.trim(),
    comments: partnerOrgLeadConvertComments(lead.contactChannel),
    ...(handlingManagerId ? { handlingManagerId } : {}),
    ...(phoneNumber ? { volunteeringCoordinatorPhone: phoneNumber } : {}),
  };
}

export type VolunteerOrgKind = "independent" | "parent" | "branch";

export function volunteerOrgKind(org: {
  parentId?: string | null;
  branches?: unknown[] | null;
}): VolunteerOrgKind {
  if (isVolunteerOrgBranch(org)) return "branch";
  return (org.branches?.length ?? 0) > 0 ? "parent" : "independent";
}

export function volunteerOrgHierarchy(org: {
  name: string;
  parentId?: string | null;
  parent?: { name?: string | null } | null;
  branches?: unknown[] | null;
}) {
  const title = org.name.trim();
  if (org.parentId) {
    return {
      kind: "branch" as const,
      title,
      parentName: org.parent?.name?.trim() || null,
    };
  }
  return {
    kind: volunteerOrgKind(org) === "parent" ? ("parent" as const) : ("independent" as const),
    title,
    parentName: null,
  };
}

export function volunteerOrgHandlingManagerName(
  org: { handlingManager?: { name?: string | null } | null },
  parent?: { handlingManager?: { name?: string | null } | null } | null,
) {
  return (
    org.handlingManager?.name?.trim() ||
    parent?.handlingManager?.name?.trim() ||
    ""
  );
}

export function volunteerOrgKindLabel(org: {
  parentId?: string | null;
  branches?: unknown[] | null;
}) {
  const kind = volunteerOrgKind(org);
  if (kind === "branch") return "שלוחה";
  if (kind === "parent") return "ארגון אב";
  return "ארגון עצמאי";
}

export function volunteerOrgKindFilter(kind: VolunteerOrgKind) {
  if (kind === "branch") {
    return { parentId: { $ne: null as unknown as string } };
  }
  if (kind === "parent") {
    return { parentId: null, branches: { $some: {} } };
  }
  return { parentId: null, branches: { $none: {} } };
}

export function volunteerOrgIsActivatable(org: {
  parentId?: string | null;
  branches?: unknown[] | null;
}) {
  if (isVolunteerOrgBranch(org)) return true;
  return (org.branches?.length ?? 0) === 0;
}

export function volunteerOrgDisplayName(
  branchName: string,
  parentName?: string | null,
) {
  const branch = branchName.trim();
  const parent = parentName?.trim();
  return parent ? `${parent} / ${branch}` : branch;
}

export function assertVolunteerOrgContactPairs(entity: VolunteerOrgContactFields) {
  for (const role of VOLUNTEER_ORG_CONTACT_ROLES) {
    const name = emptyToNull(entity[role.nameKey]);
    const phone = emptyToNull(entity[role.phoneKey]);
    if (phone && !name) {
      throw new Error(`יש למלא שם עבור ${role.label}`);
    }
    entity[role.nameKey] = name;
    entity[role.phoneKey] = phone;
  }
}

export function resetVolunteerOrgParentActivationFields(entity: {
  address?: string | null;
  coordinates?: { lat: number; lng: number; } | null;
  volunteerCapacity?: number | null;
} & VolunteerOrgEventFlags) {
  entity.address = null;
  entity.coordinates = null;
  entity.volunteerCapacity = null;
  entity.interestedInRoutine = false;
  entity.interestedInOutbreak = false;
  entity.interestedInEmergency = false;
}

export function volunteerOrgEffectiveDomainIds(
  branchDomainIds: string[],
  parentDomainIds: string[],
) {
  return [...new Set([...parentDomainIds, ...branchDomainIds])];
}

export function volunteerOrgHasOwnEventInterest(flags: VolunteerOrgEventFlags) {
  return Boolean(
    flags.interestedInRoutine ||
      flags.interestedInOutbreak ||
      flags.interestedInEmergency,
  );
}

export function volunteerOrgEffectiveEventFlags(
  unit: VolunteerOrgEventFlags,
  parent?: VolunteerOrgEventFlags | null,
): Required<VolunteerOrgEventFlags> {
  const own = {
    interestedInRoutine: Boolean(unit.interestedInRoutine),
    interestedInOutbreak: Boolean(unit.interestedInOutbreak),
    interestedInEmergency: Boolean(unit.interestedInEmergency),
  };
  if (volunteerOrgHasOwnEventInterest(own) || !parent) return own;
  return {
    interestedInRoutine: Boolean(parent.interestedInRoutine),
    interestedInOutbreak: Boolean(parent.interestedInOutbreak),
    interestedInEmergency: Boolean(parent.interestedInEmergency),
  };
}

const NO_OWN_EVENT_FLAGS = {
  interestedInRoutine: false,
  interestedInOutbreak: false,
  interestedInEmergency: false,
} as const;

export function volunteerOrgEffectiveEventWhere(
  selected: VolunteerOrgEventTypeId[],
) {
  const types = VOLUNTEER_ORG_EVENT_TYPES.filter((item) =>
    selected.includes(item.id),
  );
  if (types.length === 0) return null;
  return {
    $or: types.flatMap((item) => [
      { [item.field]: true },
      {
        ...NO_OWN_EVENT_FLAGS,
        parent: { $is: { [item.field]: true } },
      },
    ]),
  };
}

export function volunteerOrgFirstBranchSeed(parent: {
  address?: string | null;
  coordinates?: { lat: number; lng: number } | null;
  volunteerCapacity?: number | null;
} & VolunteerOrgEventFlags) {
  return {
    address: parent.address?.trim() ?? "",
    coordinates: parent.coordinates ?? null,
    volunteerCapacity:
      parent.volunteerCapacity == null ? "" : String(parent.volunteerCapacity),
    interestedInRoutine: Boolean(parent.interestedInRoutine),
    interestedInOutbreak: Boolean(parent.interestedInOutbreak),
    interestedInEmergency: Boolean(parent.interestedInEmergency),
  };
}

export function volunteerOrgActivatableFilter() {
  return {
    $or: [
      { parentId: { $ne: null as unknown as string } },
      { parentId: null, branches: { $none: {} } },
    ],
  };
}

type ActivationCarry = {
  address?: string | null;
  coordinates?: { lat: number; lng: number } | null;
  volunteerCapacity?: number | null;
} & VolunteerOrgEventFlags;

export function carryVolunteerOrgActivation<T extends ActivationCarry>(
  draft: T,
  parent: ActivationCarry,
): T {
  const ownEvents = volunteerOrgHasOwnEventInterest(draft);
  const parentEvents = volunteerOrgHasOwnEventInterest(parent)
    ? parent
    : {
        interestedInRoutine: false,
        interestedInOutbreak: false,
        interestedInEmergency: false,
      };
  const capacityBlank = draft.volunteerCapacity == null;
  const draftAddress = draft.address?.trim() ?? "";
  const address = draftAddress || parent.address?.trim() || null;
  const usingParentAddress = !draftAddress;
  return {
    ...draft,
    address,
    coordinates: draft.coordinates
      ? draft.coordinates
      : usingParentAddress
        ? (parent.coordinates ?? null)
        : null,
    volunteerCapacity: capacityBlank
      ? (parent.volunteerCapacity ?? null)
      : draft.volunteerCapacity,
    interestedInRoutine: ownEvents
      ? Boolean(draft.interestedInRoutine)
      : Boolean(parentEvents.interestedInRoutine),
    interestedInOutbreak: ownEvents
      ? Boolean(draft.interestedInOutbreak)
      : Boolean(parentEvents.interestedInOutbreak),
    interestedInEmergency: ownEvents
      ? Boolean(draft.interestedInEmergency)
      : Boolean(parentEvents.interestedInEmergency),
  };
}

export function clearedVolunteerOrgActivationFields() {
  return {
    address: null as string | null,
    coordinates: null as { lat: number; lng: number } | null,
    volunteerCapacity: null as number | null,
    interestedInRoutine: false,
    interestedInOutbreak: false,
    interestedInEmergency: false,
  };
}

export function volunteerOrgMatchesEventFilter(
  flags: VolunteerOrgEventFlags,
  selected: VolunteerOrgEventTypeId[],
) {
  if (selected.length === 0) return true;
  return selected.some((id) => {
    if (id === "routine") return Boolean(flags.interestedInRoutine);
    if (id === "outbreak") return Boolean(flags.interestedInOutbreak);
    return Boolean(flags.interestedInEmergency);
  });
}

export function volunteerOrgMatchesDomainFilter(
  effectiveDomainIds: string[],
  selectedDomainIds: string[],
) {
  if (selectedDomainIds.length === 0) return true;
  return selectedDomainIds.some((id) => effectiveDomainIds.includes(id));
}

export type VolunteerOrgActivationPhone = {
  roleId: VolunteerOrgContactRoleKind;
  label: string;
  name: string;
  phone: string;
  email: string;
  fromParent: boolean;
};

function activationRows(source: VolunteerOrgContactFields & {
  contacts?: VolunteerOrgStoredContact[] | null;
}) {
  return volunteerOrgContactDrafts(source);
}

export function volunteerOrgActivationPhones(
  branch: VolunteerOrgContactFields & {
    contacts?: VolunteerOrgStoredContact[] | null;
  },
  parent?: (VolunteerOrgContactFields & {
    contacts?: VolunteerOrgStoredContact[] | null;
  }) | null,
): VolunteerOrgActivationPhone[] {
  const branchRows = activationRows(branch);
  const parentRows = parent ? activationRows(parent) : [];
  const result: VolunteerOrgActivationPhone[] = [];

  for (const role of CANONICAL_VOLUNTEER_ORG_CONTACTS) {
    const branchMatch = branchRows.find(
      (row) => row.roleKind === role.roleKind && row.name && row.phone,
    );
    if (branchMatch) {
      result.push({
        roleId: role.roleKind,
        label: branchMatch.roleLabel || role.roleLabel,
        name: branchMatch.name,
        phone: branchMatch.phone,
        email: branchMatch.email,
        fromParent: false,
      });
      continue;
    }
    const parentMatch = parentRows.find(
      (row) => row.roleKind === role.roleKind && row.name && row.phone,
    );
    if (parentMatch) {
      result.push({
        roleId: role.roleKind,
        label: parentMatch.roleLabel || role.roleLabel,
        name: parentMatch.name,
        phone: parentMatch.phone,
        email: parentMatch.email,
        fromParent: true,
      });
    }
  }

  for (const row of branchRows) {
    if (row.roleKind !== "other" || !row.name || !row.phone) continue;
    result.push({
      roleId: "other",
      label: row.roleLabel || "איש קשר",
      name: row.name,
      phone: row.phone,
      email: row.email,
      fromParent: false,
    });
  }
  return result;
}

export type VolunteerOrgActivationWarning =
  | "no-phone"
  | "no-map"
  | "no-event-type"
  | "no-domains";

export function volunteerOrgActivationWarnings(input: {
  phones: VolunteerOrgActivationPhone[];
  coordinates: unknown;
  flags: VolunteerOrgEventFlags;
  effectiveDomainIds: string[];
}): VolunteerOrgActivationWarning[] {
  const warnings: VolunteerOrgActivationWarning[] = [];
  if (input.phones.length === 0) warnings.push("no-phone");
  if (!hasValidCoordinates(input.coordinates)) warnings.push("no-map");
  if (
    !input.flags.interestedInRoutine &&
    !input.flags.interestedInOutbreak &&
    !input.flags.interestedInEmergency
  ) {
    warnings.push("no-event-type");
  }
  if (input.effectiveDomainIds.length === 0) warnings.push("no-domains");
  return warnings;
}

export const VOLUNTEER_ORG_WARNING_LABELS: Record<
  VolunteerOrgActivationWarning,
  string
> = {
  "no-phone": "אין טלפון",
  "no-map": "אין נקודה במפה",
  "no-event-type": "אין סוג אירוע",
  "no-domains": "אין תחומים",
};

export function volunteerOrgCommunicationRecordsWhere(
  volunteerOrganizationId: string,
) {
  return { volunteerOrganizationId };
}

export function namesConflict(
  left: string,
  right: string,
) {
  return left.trim().toLowerCase() === right.trim().toLowerCase();
}
